'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ProductData } from '../types';

interface TrendProductItemPCProps {
  product: ProductData;
  index: number;
  description?: string;
  onClick?: () => void;
  onExposure?: () => void;
}

export default function TrendProductItemPC({
  product,
  index,
  description,
  onClick,
  onExposure,
}: TrendProductItemPCProps) {
  const [isHovered, setIsHovered] = useState(false);
  const t = useTranslations('activity.springSale');
  // 获取当前货币信息和用户信息
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  // 曝光检测
  const { ref: expRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && onExposure) {
      onExposure();
    }
  }, [inView, onExposure]);

  // 获取价格信息
  const productPriceInfo = useMemo(() => {
    return priceInfo(userInfo, product, currency);
  }, [userInfo, product, currency]);

  // 获取模特图（material/celebrity）或商品图
  const materialImage = product.image?.celebrity?.[0] || product.image?.material?.[0];
  const productGrayImage = product.image?.product_gray?.[0] || product.image?.product?.[0];

  // 外层始终显示模特图，不受hover影响
  const displayImage = materialImage || productGrayImage;

  return (
    <div ref={expRef} className="relative w-full">
      <MagicJump
        prefetch
        item={{
          jump_type: 2,
          Jump_target: product.shop_sku,
          sku: { shop_sku: product.shop_sku },
        }}
        onBeforeJump={onClick}
        aria-label={product.short_name || product.name}
        className="block rounded-[8px] overflow-hidden"
      >
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* 主图 */}
          <Image
            src={displayImage}
            alt={((description || product.short_name || product.name) ?? '').slice(0, 70)}
            width={438}
            height={597}
            loading={index < 6 ? 'eager' : 'lazy'}
            className={`w-full h-full ${materialImage ? 'object-cover' : 'object-contain mix-blend-multiply'}`}
            style={{
              aspectRatio: '0.7336',
            }}
          />

          {/* Hover悬停信息层 - 与StyleHub保持一致 */}
          {isHovered && (
            <div className="absolute inset-0 bg-black/60 py-[40px] px-[15px] lg:py-[48px] lg:px-[20px] 2xl:py-[40px] 2xl:px-[24px] box-border">
              <div className="bg-[#eeeeee] w-full h-full flex flex-col items-center justify-center p-[20px] lg:p-[24px] pt-[30px] lg:pt-[40px] 2xl:pt-[70px] min-h-[200px] min-w-[150px]">
                {/* 产品灰底图 */}
                <Image
                  src={product.image?.product?.[0]}
                  alt={product.short_name || product.name}
                  width={315}
                  height={158}
                  className="w-[150px] lg:w-[250px] 2xl:w-[315px] h-auto object-contain mix-blend-multiply flex-shrink-0"
                  style={{ aspectRatio: '2/1' }}
                />

                {/* 产品信息 */}
                <div className="mt-[16px] lg:mt-[30px] 2xl:mt-[40px] 3xl:mt-[90px] text-center flex-grow flex flex-col flex-start">
                  <p className="text-[11px] lg:text-[13px] 2xl:text-[14px] text-[#282828] leading-tight mb-[30px] lg:mb-[20px] 2xl:mb-[25px]">
                    <span className="font-br block lg:inline">
                      {product.short_name || product.name}
                    </span>
                    {productPriceInfo?.sell_price && (
                      <span className="ml-0 lg:ml-[8px] font-bb block lg:inline mt-[4px] lg:mt-0">
                        {formatPriceNumberWithSymbol(productPriceInfo.sell_price, currency)}
                      </span>
                    )}
                  </p>

                  {/* Shop Now 按钮 */}
                  <div className="w-[120px] lg:w-[140px] 2xl:w-[120px] h-[30px] lg:h-[35px] 2xl:h-[30px] xl:h-[48px] mx-auto flex items-center justify-center rounded-[36px] border border-black text-[12px] lg:text-[13px] 2xl:text-[16px] text-[#282828] font-br">
                    {t('shopNow')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 商品信息 - 显示在图片下方 */}
        <div className="bg-white p-[16px]">
          <h3 className="line-clamp-1 text-[#282828] text-[16px] leading-[19px] font-bb">
            {product.name || product.short_name}
          </h3>
          {description && (
            <p className="text-[14px] leading-[17px] text-gray-600 line-clamp-2 mt-[12px] h-34">
              {description}
            </p>
          )}
        </div>
      </MagicJump>
    </div>
  );
}
